import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "navbar" }
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "navbar-search-group" }
const _hoisted_4 = { class: "navbar-nav" }
const _hoisted_5 = ["aria-expanded"]
const _hoisted_6 = {
  key: 0,
  class: "us-alerts-badge"
}
const _hoisted_7 = {
  key: 0,
  class: "us-alerts-menu"
}
const _hoisted_8 = {
  key: 0,
  class: "us-alerts-empty"
}
const _hoisted_9 = {
  key: 1,
  class: "us-alerts-empty"
}
const _hoisted_10 = { class: "us-alert-summary" }
const _hoisted_11 = { class: "us-alert-order" }
const _hoisted_12 = { class: "us-alert-context" }
const _hoisted_13 = {
  key: 0,
  class: "us-alert-details"
}
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { key: 2 }
const _hoisted_17 = { key: 3 }
const _hoisted_18 = { class: "us-alert-actions" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["onClick"]
const _hoisted_21 = ["onClick"]
const _hoisted_22 = ["aria-expanded"]
const _hoisted_23 = { class: "navbar-mobile-container" }
const _hoisted_24 = ["aria-expanded"]
const _hoisted_25 = { class: "navbar-mobile-buttons" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = ["href"]
const _hoisted_28 = ["aria-expanded"]
const _hoisted_29 = {
  key: 0,
  class: "dropdown-loading"
}

import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getVendeurGeneralInfo } from "@/api/vendeur";
import {
  adminEfpGetNotifications,
  adminEfpMarkNotificationRead,
  adminEfpNotificationsStreamUrl,
  adminEfpResolveNotification,
  type AdminEfpNotification,
} from "@/api/adminEfp";
import { useAuthStore } from "@/stores/auth";
import type { NavbarProps, NavbarEmits } from "@/types";
import { useAdminEfpStore } from "@/stores/adminEfp";
import AdminAcheteurSearch from "@/components/adminEFP/AdminAcheteurSearch.vue";
import AdminVendeurSearch from "@/components/adminEFP/AdminVendeurSearch.vue";

const LOCAL_STORAGE_KEY = "vendeur_info_header";


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminNavbar',
  props: {
    isLoading: { type: Boolean },
    vendeurData: {},
    isSidebarCollapsed: { type: Boolean },
    isSidebarOpen: { type: Boolean }
  },
  emits: ["toggle-sidebar-collapsed", "toggle-sidebar", "show-contact-modal", "show-user-modal"],
  setup(__props: any, { emit: __emit }) {

const adminEfp      = useAdminEfpStore();
const logoutLoading = ref(false);


const emit = __emit;

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isDashboardRoute = computed(() => {
  return route.path.startsWith("/dashboard");
});

// Génère un slug URL-friendly à partir d'une chaîne
const getSlug = (str: string): string => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
    .replace(/[^a-z0-9]+/g, "-") // Remplace les caractères spéciaux par des tirets
    .replace(/^-+|-+$/g, ""); // Supprime les tirets en début/fin
};

// URL de la page vendeur
const vendeurPageUrl = computed(() => {
  const nomBoutique = vendeurData.value.nomBoutique;
  const idVendeur = vendeurData.value.id_vendeur;
  if (!nomBoutique || !idVendeur) return "";
  const slug = getSlug(nomBoutique);
  return `https://www.efashion-paris.com/vendeurs/${slug}-${idVendeur}.html`;
});

// Dropdown
const dropdownRef = ref<HTMLElement | null>(null);
const usAlertsRef = ref<HTMLElement | null>(null);
const dropdownMobileRef = ref<HTMLElement | null>(null);
const isDropdownOpen = ref(false);
const isDropdownMobileOpen = ref(false);
const isUsAlertsOpen = ref(false);
const usAlertsLoading = ref(false);
const adminNotifications = ref<AdminEfpNotification[]>([]);
const expandedNotificationIds = ref<Set<number>>(new Set());
let notificationsEventSource: EventSource | null = null;

const handleClickOutside = (event: Event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
  if (usAlertsRef.value && !usAlertsRef.value.contains(event.target as Node)) {
    isUsAlertsOpen.value = false;
  }
  if (
    dropdownMobileRef.value &&
    !dropdownMobileRef.value.contains(event.target as Node)
  ) {
    isDropdownMobileOpen.value = false;
  }
};

const loadUsOrderAlerts = async () => {
  usAlertsLoading.value = true;
  try {
    const response = await adminEfpGetNotifications();
    adminNotifications.value = response.items;
  } catch {
    adminNotifications.value = [];
  } finally {
    usAlertsLoading.value = false;
  }
};

const toggleUsAlerts = () => {
  isUsAlertsOpen.value = !isUsAlertsOpen.value;
  if (isUsAlertsOpen.value) void loadUsOrderAlerts();
};

const formatUsOrderDate = (value: string | null) => {
  if (!value) return "Date inconnue";
  const parsed = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
};

const notificationPayloadValue = (
  notification: AdminEfpNotification,
  key: string,
): string | null => {
  const value = notification.payload?.[key];
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : null;
};

const notificationSellerName = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "sellerName");

const notificationBuyerName = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "buyerName");

const notificationContact = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "email") ||
  notificationPayloadValue(notification, "buyerEmail");

const notificationCompactContext = (notification: AdminEfpNotification) => {
  if (notification.type === "shopify_privacy_request") {
    return notificationPayloadValue(notification, "shopDomain")
      || `Reçue le ${formatUsOrderDate(notification.createdAt)}`;
  }
  if (notification.type === "store_order_created") {
    const product = notificationPayloadValue(notification, "product");
    const amount = notificationPayloadValue(notification, "amount");
    if (product) {
      return amount ? `${product} · ${Number(amount).toFixed(2)} € HT` : product;
    }
  }
  const orderDate = notificationPayloadValue(notification, "orderDate");
  if (orderDate) {
    const destination = notificationPayloadValue(notification, "destinationCode");
    return `Commandée le ${formatUsOrderDate(orderDate)}${destination ? ` · ${destination}` : ""}`;
  }
  const registeredAt = notificationPayloadValue(notification, "registeredAt");
  if (registeredAt) {
    return `Inscrit le ${formatUsOrderDate(registeredAt)}`;
  }
  return `Reçue le ${formatUsOrderDate(notification.createdAt)}`;
};

const isNotificationExpanded = (idNotification: number) =>
  expandedNotificationIds.value.has(idNotification);

const toggleNotificationDetails = (idNotification: number) => {
  const next = new Set(expandedNotificationIds.value);
  if (next.has(idNotification)) next.delete(idNotification);
  else next.add(idNotification);
  expandedNotificationIds.value = next;
};

const openNotification = async (notification: AdminEfpNotification) => {
  if (!notification.isRead) {
    try {
      await adminEfpMarkNotificationRead(notification.idNotification);
      notification.isRead = true;
    } catch {
      // La navigation reste disponible si l'enregistrement de lecture échoue.
    }
  }
  isUsAlertsOpen.value = false;
  if (notification.entityType === "shopify_privacy_request") {
    await router.push({ name: "adminEFP/shopify" });
    return;
  }
  if (notification.entityType === "store_order") {
    await router.push({ name: "adminEFP/store-orders" });
    return;
  }
  if (notification.idCommande) {
    await router.push({
      name: "adminEFP/orders",
      query: { commande_id: String(notification.idCommande) },
    });
  } else if (notification.entityType === "seller" && notification.entityId) {
    await router.push({
      name: "adminEFP/vendeur-fiche",
      params: { id: String(notification.entityId) },
    });
  }
};

const resolveNotification = async (idNotification: number) => {
  await adminEfpResolveNotification(idNotification);
  adminNotifications.value = adminNotifications.value.filter(
    (notification) => notification.idNotification !== idNotification,
  );
};

const connectNotificationsStream = () => {
  notificationsEventSource?.close();
  notificationsEventSource = new EventSource(
    adminEfpNotificationsStreamUrl(),
    { withCredentials: true },
  );
  const reload = () => void loadUsOrderAlerts();
  notificationsEventSource.addEventListener("notification.created", reload);
  notificationsEventSource.addEventListener("notification.read", reload);
  notificationsEventSource.addEventListener("notification.resolved", reload);
};

const DEFAULT_VENDEUR = {
  nomSociete: "non disponible",
  email: "non disponible",
  telephone: "non disponible",
  mobile: "non disponible",
  adresse: "non disponible",
  code_postal: "non disponible",
  nomBoutique: "",
  id_vendeur: 0,
};

const isLoading = ref(true);
const vendeurData = ref({ ...DEFAULT_VENDEUR });

const loadFromStorage = () => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) {
    isLoading.value = false;
    return;
  }

  try {
    const saved = JSON.parse(raw) as Partial<typeof DEFAULT_VENDEUR>;
    vendeurData.value = {
      ...DEFAULT_VENDEUR,
      ...saved,
    };
  } catch (e) {
    // Erreur silencieuse - les infos vendeur restent vides
  } finally {
    isLoading.value = false;
  }
};


async function adminLogout() {
  logoutLoading.value = true;
  try {
    await adminEfp.logout();
    await router.replace({ name: "adminEFP/auth" });
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    authStore.logout();
  } catch {
    // error.value = "Erreur lors de la déconnexion";
  } finally {
    logoutLoading.value = false;
  }
}


//save grossiste info into local storage and state
const fetchVendeurInfo = async () => {
  const profile = await getVendeurGeneralInfo();

  const storeAddress = profile.adresses?.find((addr: any) => addr.type === 2);

  const newData = {
    nomSociete: profile.nomSociete || profile.nomBoutique || "non disponible",
    email: profile.email || "non disponible",
    telephone: storeAddress?.telephone || "non disponible",
    mobile: storeAddress?.mobile || "non disponible",
    adresse: storeAddress?.adresse || "non disponible",
    code_postal: storeAddress?.codePostal || "non disponible",
    nomBoutique: profile.nomBoutique || "",
    id_vendeur: profile.id_vendeur || 0,
  };

  vendeurData.value = newData;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newData));
};

const refreshFromApiIfNeeded = async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) return;

  const authVendeurId = authStore.user?.id_vendeur ?? authStore.user?.id;
  const cachedId = vendeurData.value.id_vendeur;

  if (
    vendeurData.value.nomBoutique &&
    cachedId &&
    authVendeurId &&
    cachedId === authVendeurId
  ) {
    return;
  }

  isLoading.value = true;
  try {
    await fetchVendeurInfo();
  } catch (error) {
    // Erreur silencieuse - les infos vendeur n'ont pas été chargées
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  loadFromStorage();

  void refreshFromApiIfNeeded();
  void loadUsOrderAlerts();
  connectNotificationsStream();
});

watch(
  () => route.path,
  () => {
    void refreshFromApiIfNeeded();
  },
);

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  notificationsEventSource?.close();
});

const toggleSidebarCollapsed = () => {
  emit("toggle-sidebar-collapsed");
};

const toggleSidebar = () => {
  emit("toggle-sidebar");
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const toggleDropdownMobile = () => {
  isDropdownMobileOpen.value = !isDropdownMobileOpen.value;
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("header", _hoisted_1, [
    _createElementVNode("button", {
      onClick: toggleSidebarCollapsed,
      class: _normalizeClass(["sidebar-toggle-btn", { 'sidebar-collapsed': _ctx.isSidebarCollapsed }]),
      title: 
        _ctx.isSidebarCollapsed ? 'Étendre la sidebar' : 'Rétracter la sidebar'
      
    }, _cache[1] || (_cache[1] = [
      _createElementVNode("i", { class: "fas fa-bars" }, null, -1)
    ]), 10, _hoisted_2),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(AdminAcheteurSearch),
      _createVNode(AdminVendeurSearch)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", {
        class: "us-alerts",
        ref_key: "usAlertsRef",
        ref: usAlertsRef
      }, [
        _createElementVNode("button", {
          type: "button",
          class: "us-alerts-button",
          "aria-label": "Notifications administrateur",
          "aria-expanded": isUsAlertsOpen.value,
          onClick: _withModifiers(toggleUsAlerts, ["stop"])
        }, [
          _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-bell" }, null, -1)),
          (adminNotifications.value.length)
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(adminNotifications.value.length > 99 ? "99+" : adminNotifications.value.length), 1))
            : _createCommentVNode("", true)
        ], 8, _hoisted_5),
        (isUsAlertsOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "us-alerts-title" }, "Notifications", -1)),
              (usAlertsLoading.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_8, "Chargement…"))
                : (!adminNotifications.value.length)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Aucune notification à traiter. "))
                  : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(adminNotifications.value, (notification) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: notification.idNotification,
                        class: _normalizeClass(["us-alert-item", { 'us-alert-item-unread': !notification.isRead }])
                      }, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(notification.title), 1),
                          _createElementVNode("span", _hoisted_12, _toDisplayString(notificationCompactContext(notification)), 1),
                          _createElementVNode("span", {
                            class: _normalizeClass(notification.isRead ? 'text-amber-700' : 'text-red-700')
                          }, _toDisplayString(notification.isRead ? "Lue · à traiter" : "Non lue"), 3)
                        ]),
                        (isNotificationExpanded(notification.idNotification))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              (notification.message)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(notification.message), 1))
                                : _createCommentVNode("", true),
                              (notificationSellerName(notification))
                                ? (_openBlock(), _createElementBlock("span", _hoisted_15, " Vendeur : " + _toDisplayString(notificationSellerName(notification)), 1))
                                : _createCommentVNode("", true),
                              (notificationBuyerName(notification))
                                ? (_openBlock(), _createElementBlock("span", _hoisted_16, " Acheteur : " + _toDisplayString(notificationBuyerName(notification)), 1))
                                : _createCommentVNode("", true),
                              (notificationContact(notification))
                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, " Contact : " + _toDisplayString(notificationContact(notification)), 1))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_18, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "us-alert-secondary",
                            onClick: _withModifiers(($event: any) => (toggleNotificationDetails(notification.idNotification)), ["stop"])
                          }, _toDisplayString(isNotificationExpanded(notification.idNotification) ? "Masquer" : "Voir détails"), 9, _hoisted_19),
                          _createElementVNode("button", {
                            type: "button",
                            class: "us-alert-secondary",
                            onClick: ($event: any) => (openNotification(notification))
                          }, " Ouvrir ", 8, _hoisted_20),
                          _createElementVNode("button", {
                            type: "button",
                            class: "us-alert-resolve",
                            onClick: _withModifiers(($event: any) => (resolveNotification(notification.idNotification)), ["stop"])
                          }, " Traiter ", 8, _hoisted_21)
                        ])
                      ], 2))
                    }), 128))
            ]))
          : _createCommentVNode("", true)
      ], 512),
      _createElementVNode("div", {
        class: "navbar-dropdown",
        ref_key: "dropdownRef",
        ref: dropdownRef
      }, [
        _createElementVNode("button", {
          class: "navbar-dropdown-toggle",
          onClick: toggleDropdown,
          "aria-expanded": isDropdownOpen.value
        }, _cache[4] || (_cache[4] = [
          _createElementVNode("i", { class: "fas fa-user" }, null, -1)
        ]), 8, _hoisted_22),
        _createElementVNode("ul", {
          class: _normalizeClass(["navbar-dropdown-menu", isDropdownOpen.value ? 'is-open' : 'is-closed'])
        }, [
          _createElementVNode("li", {
            class: "dropdown-item dropdown-logout",
            onClick: adminLogout
          }, _cache[5] || (_cache[5] = [
            _createElementVNode("i", { class: "fas fa-sign-out-alt" }, null, -1),
            _createElementVNode("a", { href: "javascript:void(0);" }, "Déconnexion", -1)
          ]))
        ], 2)
      ], 512)
    ]),
    _createElementVNode("div", _hoisted_23, [
      _createElementVNode("button", {
        type: "button",
        onClick: toggleSidebar,
        "aria-controls": "sidebarMenu",
        "aria-expanded": _ctx.isSidebarOpen,
        "aria-label": "Toggle navigation",
        class: "navbar-mobile-burger"
      }, _cache[6] || (_cache[6] = [
        _createElementVNode("i", { class: "fas fa-bars" }, null, -1)
      ]), 8, _hoisted_24),
      _createElementVNode("ul", _hoisted_25, [
        (vendeurPageUrl.value)
          ? (_openBlock(), _createElementBlock("li", _hoisted_26, [
              _createElementVNode("a", {
                href: vendeurPageUrl.value,
                class: "navbar-btn-secondary",
                target: "_blank"
              }, _cache[7] || (_cache[7] = [
                _createElementVNode("i", { class: "fas fa-store" }, null, -1),
                _createElementVNode("span", null, "Ma page", -1)
              ]), 8, _hoisted_27)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("li", null, [
          _createElementVNode("div", {
            class: "navbar-dropdown",
            ref_key: "dropdownMobileRef",
            ref: dropdownMobileRef
          }, [
            _createElementVNode("button", {
              class: "navbar-dropdown-toggle",
              onClick: toggleDropdownMobile,
              "aria-expanded": isDropdownMobileOpen.value
            }, _cache[8] || (_cache[8] = [
              _createElementVNode("i", { class: "fas fa-user" }, null, -1)
            ]), 8, _hoisted_28),
            _createElementVNode("ul", {
              class: _normalizeClass(["navbar-dropdown-menu", isDropdownMobileOpen.value ? 'is-open' : 'is-closed'])
            }, [
              (isLoading.value)
                ? (_openBlock(), _createElementBlock("li", _hoisted_29, _cache[9] || (_cache[9] = [
                    _createElementVNode("i", { class: "fas fa-spinner fa-spin" }, null, -1),
                    _createElementVNode("span", null, "Chargement des informations...", -1)
                  ])))
                : (_openBlock(), _createElementBlock("li", {
                    key: 1,
                    class: "dropdown-item dropdown-logout",
                    onClick: _cache[0] || (_cache[0] = 
                    () => {
                      isDropdownMobileOpen.value = false;
                      adminLogout();
                    }
                  )
                  }, _cache[10] || (_cache[10] = [
                    _createElementVNode("i", { class: "fas fa-sign-out-alt" }, null, -1),
                    _createElementVNode("a", { href: "#" }, "Déconnexion", -1)
                  ])))
            ], 2)
          ], 512)
        ])
      ])
    ])
  ]))
}
}

})